import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Sparkles, FileDown, Loader2, CheckCircle2, ArrowUpRight, Quote, Target,
  MessageSquareQuote, GraduationCap, Lightbulb, ListChecks, BrainCircuit, Compass, Heart, Info,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const COMPETENCY_DESCRIPTORS = {
  "Managing Others": "Directing and leading others to accomplish organizational and team goals and objectives.",
  "Communication": "Communicating clearly and effectively with people inside and outside of the organization.",
  "Motivating Others": "Inspiring others to perform well by actively conveying enthusiasm and a passion for doing a good job.",
  "Delivering Results": "Maintaining a high level of commitment to personally getting things done.",
  "Coaching And Developing": "Advising, assisting, mentoring and giving feedback to encourage development of work-related competencies and long-term career growth.",
  "Action Oriented": "Consistently maintains high activity/productivity, working with vigour, effectiveness and determination over a sustained period.",
};

function CompetencyName({ name }) {
  const desc = COMPETENCY_DESCRIPTORS[name];
  if (!desc) return <span className="font-medium text-stone-800">{name}</span>;
  return (
    <TooltipProvider delayDuration={150}>
      <span className="font-medium text-stone-800 inline-flex items-center gap-1">
        {name}
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" data-testid={`competency-info-${name.replace(/\s+/g, "-")}`}
              className="text-stone-400 hover:text-[#b81f24] transition-colors" aria-label={`What is ${name}`}>
              <Info className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
            {desc}
          </TooltipContent>
        </Tooltip>
      </span>
    </TooltipProvider>
  );
}

const LEVEL_COLOR = {
  Emerging: { bg: "#fee2e2", text: "#991b1b" },
  Developing: { bg: "#fef3c7", text: "#92400e" },
  Proficient: { bg: "#dbeafe", text: "#1e40af" },
  Strong: { bg: "#ccfbf1", text: "#115e59" },
  Exceptional: { bg: "#dcfce7", text: "#166534" },
};

function Section({ n, title, icon: Icon, children }) {
  return (
    <section className="bg-white border border-stone-200 rounded-3xl p-6 sm:p-7" data-testid={`report-section-${n}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-8 rounded-lg bg-[#b81f24]/10 text-[#b81f24] grid place-items-center text-sm font-semibold">{n}</span>
        {Icon && <Icon className="w-4 h-4 text-stone-400" strokeWidth={1.75} />}
        <h3 className="font-display text-lg font-semibold text-stone-900">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Bar({ value, max = 100 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color = max === 10
    ? (value >= 8 ? "#15803d" : value >= 6 ? "#b45309" : "#b91c1c")
    : (value >= 80 ? "#15803d" : value >= 65 ? "#b45309" : "#b91c1c");
  return (
    <div className="h-2 rounded-full bg-stone-100 overflow-hidden w-full">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color, transition: "width .8s" }} />
    </div>
  );
}

function ReportBody({ r, sessionId }) {
  const [dl, setDl] = useState(false);
  const lvl = LEVEL_COLOR[r.performance_level] || LEVEL_COLOR.Developing;

  const downloadPdf = async () => {
    setDl(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/coaching-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `coaching-report-${sessionId.slice(-6)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Coaching report downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setDl(false); }
  };

  return (
    <div className="space-y-6" data-testid="coaching-report-body">
      {/* 1 + 2 */}
      <section className="bg-stone-900 text-stone-50 rounded-3xl p-7 sm:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-[#b81f24]/30 blur-3xl" aria-hidden />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[10px] tracking-[0.18em] uppercase text-stone-400 mb-2">Overall performance summary</div>
            <p className="text-stone-200 leading-relaxed text-sm" data-testid="report-summary">{r.overall_summary}</p>
          </div>
          <div className="text-center shrink-0">
            <div className="font-display text-6xl font-light" data-testid="report-overall-score">{r.overall_score}</div>
            <div className="text-xs text-stone-400 mb-3">out of 100</div>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: lvl.bg, color: lvl.text }}>
              {r.performance_level}
            </span>
          </div>
        </div>
        <div className="relative mt-6">
          <Button onClick={downloadPdf} disabled={dl} data-testid="download-coaching-pdf-btn"
            className="rounded-md bg-stone-50 text-stone-900 hover:bg-white h-10 px-5">
            <FileDown className="w-4 h-4 mr-1.5" /> {dl ? "Preparing…" : "Download full report (PDF)"}
          </Button>
        </div>
      </section>

      {/* 3 Competency performance */}
      <Section n={3} title="Competency Performance" icon={Target}>
        {(() => {
          const compScores = (r.competency_performance || []).map(c => c.score).filter(v => v != null);
          const compMax = compScores.length && Math.max(...compScores) <= 10 ? 10 : 100;
          return (
            <div className="grid sm:grid-cols-2 gap-4">
              {r.competency_performance.map((c, i) => (
                <div key={i} className="border border-stone-100 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <CompetencyName name={c.competency} />
                    <span className="font-display text-lg font-semibold text-stone-900" data-testid={`competency-score-${i}`}>{c.score}</span>
                  </div>
                  <Bar value={c.score} max={compMax} />
                  {c.rating && <div className="text-xs text-stone-500 mt-1.5">{c.rating}</div>}
              {c.demonstrated_well?.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">Strengths</div>
                  <ul className="text-sm text-stone-600 space-y-0.5">{c.demonstrated_well.map((x, j) => <li key={j}>• {x}</li>)}</ul>
                </div>
              )}
              {c.missed_opportunities?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wide text-orange-700 mb-1">Improve</div>
                  <ul className="text-sm text-stone-600 space-y-0.5">{c.missed_opportunities.map((x, j) => <li key={j}>• {x}</li>)}</ul>
                </div>
              )}
              {c.impact && <div className="text-xs text-stone-500 mt-2 italic">Impact: {c.impact}</div>}
                </div>
              ))}
            </div>
          );
        })()}
      </Section>

      {/* 4 Strengths */}
      <Section n={4} title="Behavioural Strengths" icon={CheckCircle2}>
        <div className="space-y-4">
          {r.behavioural_strengths.map((s, i) => (
            <div key={i} className="border-l-2 border-emerald-500 pl-4">
              <div className="font-medium text-stone-800">{s.title}</div>
              <div className="text-sm text-stone-600 mt-0.5">{s.behaviour}</div>
              {s.evidence && <div className="text-sm text-stone-700 italic mt-1.5 flex gap-1.5"><Quote className="w-3.5 h-3.5 text-stone-400 mt-1 shrink-0" />{s.evidence}</div>}
              {s.why_it_mattered && <div className="text-xs text-emerald-700 mt-1">Why it mattered: {s.why_it_mattered}</div>}
            </div>
          ))}
        </div>
      </Section>

      {/* 5 Growth */}
      <Section n={5} title="Growth Opportunities" icon={ArrowUpRight}>
        <div className="space-y-4">
          {r.growth_opportunities.map((g, i) => (
            <div key={i} className="border-l-2 border-orange-500 pl-4">
              <div className="font-medium text-stone-800">{g.title}</div>
              <div className="text-sm text-stone-600 mt-0.5">{g.behaviour}</div>
              {g.why_reduced_effectiveness && <div className="text-xs text-orange-700 mt-1">Why it reduced effectiveness: {g.why_reduced_effectiveness}</div>}
              {g.better_alternative && <div className="text-sm text-stone-700 mt-1.5 bg-amber-50 rounded-lg px-3 py-2">Better: <span className="italic">{g.better_alternative}</span></div>}
            </div>
          ))}
        </div>
      </Section>

      {/* 6 Timeline */}
      <Section n={6} title="Conversation Timeline" icon={Compass}>
        <div className="space-y-3">
          {r.conversation_timeline.map((t, i) => (
            <div key={i} className="flex gap-4 items-start">
              <div className="w-36 shrink-0">
                <div className="text-sm font-medium text-stone-800">{t.stage}</div>
                <div className="flex items-center gap-2 mt-1"><span className="text-xs tabular-nums text-stone-500 w-7">{t.score}</span><Bar value={t.score} /></div>
              </div>
              <div className="text-sm text-stone-600 flex-1">{t.observation}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 7 Communication analysis */}
      <Section n={7} title="Communication Analysis" icon={MessageSquareQuote}>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {r.communication_analysis.map((c, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-1"><span className="text-sm text-stone-700">{c.dimension}</span><span className="text-sm font-semibold tabular-nums">{c.score}</span></div>
              <Bar value={c.score} />
              <div className="text-xs text-stone-500 mt-1.5"><span className="text-emerald-700">+</span> {c.strength}</div>
              <div className="text-xs text-stone-500"><span className="text-orange-700">→</span> {c.opportunity}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8 AI employee analysis */}
      <Section n={8} title="AI Employee Response Analysis" icon={BrainCircuit}>
        <p className="text-sm text-stone-700 leading-relaxed">{r.ai_employee_analysis}</p>
      </Section>

      {/* 9 Missed opportunities */}
      <Section n={9} title="Missed Coaching Opportunities" icon={MessageSquareQuote}>
        <div className="space-y-4">
          {r.missed_coaching_opportunities.map((m, i) => (
            <div key={i} className="bg-stone-50 rounded-2xl p-4">
              <div className="text-sm font-medium text-stone-800">{m.situation}</div>
              <div className="text-sm text-stone-600 mt-1.5">You said: <span className="italic">"{m.participant_response}"</span></div>
              <div className="text-sm text-stone-800 mt-1.5">Better: <span className="italic text-[#b81f24]">"{m.suggested_better_response}"</span></div>
              {m.why_better && <div className="text-xs text-stone-500 mt-1">{m.why_better}</div>}
            </div>
          ))}
        </div>
      </Section>

      {/* 10 Expert manager */}
      <Section n={10} title="What an Expert Manager Would Do" icon={GraduationCap}>
        <div className="space-y-3">
          {r.expert_manager_examples.map((e, i) => (
            <div key={i} className="border border-stone-100 rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wide text-stone-500 mb-1">{e.moment}</div>
              <div className="text-sm text-stone-800 italic">"{e.example_response}"</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 11 Reflection */}
      <Section n={11} title="Reflection Questions" icon={Lightbulb}>
        <ol className="space-y-2">
          {r.reflection_questions.map((q, i) => (
            <li key={i} className="flex gap-3 text-sm text-stone-700"><span className="text-stone-400">{i + 1}.</span>{q}</li>
          ))}
        </ol>
      </Section>

      {/* 12 Action plan */}
      <Section n={12} title="Personalized Action Plan" icon={ListChecks}>
        <div className="grid sm:grid-cols-3 gap-3">
          {r.action_plan.map((a, i) => (
            <div key={i} className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4">
              <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1.5">{a.type}</div>
              <div className="text-sm text-stone-700">{a.action}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 13 Learning resources */}
      <Section n={13} title="Learning Resources" icon={Sparkles}>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-stone-500 mb-1">Behaviours to practice</div>
            <ul className="text-stone-700 space-y-0.5">{r.learning_resources.behaviours_to_practice.map((b, i) => <li key={i}>• {b}</li>)}</ul>
          </div>
          <div className="space-y-2">
            <div><span className="text-xs uppercase tracking-wide text-stone-500">Coaching model:</span> <span className="text-stone-700">{r.learning_resources.coaching_model}</span></div>
            <div><span className="text-xs uppercase tracking-wide text-stone-500">Communication tip:</span> <span className="text-stone-700">{r.learning_resources.communication_tip}</span></div>
            <div><span className="text-xs uppercase tracking-wide text-stone-500">Reflective exercise:</span> <span className="text-stone-700">{r.learning_resources.reflective_exercise}</span></div>
          </div>
        </div>
      </Section>

      {/* 14 Readiness */}
      <Section n={14} title="Readiness for More Difficult Scenarios" icon={Compass}>
        <div className="inline-block bg-[#b81f24]/10 text-[#b81f24] rounded-full px-4 py-1.5 text-sm font-semibold mb-2">{r.readiness.recommendation}</div>
        <p className="text-sm text-stone-700">{r.readiness.explanation}</p>
      </Section>

      {/* Closing */}
      <div className="bg-white border border-stone-200 rounded-3xl p-7 text-center">
        <Heart className="w-6 h-6 text-[#b81f24] mx-auto mb-3" strokeWidth={1.75} />
        <p className="text-stone-800 font-display text-lg leading-relaxed max-w-2xl mx-auto" data-testid="report-closing">{r.closing_message}</p>
      </div>
    </div>
  );
}

export default function CoachingReport({ sessionId }) {
  const [state, setState] = useState({ status: "loading", report: null });
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const fetchReport = useCallback(async () => {
    const res = await api.get(`/sessions/${sessionId}/coaching-report`);
    return res.data;
  }, [sessionId]);

  useEffect(() => {
    let active = true;
    fetchReport().then((d) => {
      if (!active) return;
      setState({ status: d.status === "ready" ? "ready" : (d.status === "generating" ? "generating" : "not_generated"), report: d.report });
      if (d.status === "generating") startPolling();
    }).catch(() => active && setState({ status: "not_generated", report: null }));
    return () => { active = false; stopPoll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startPolling = () => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const d = await fetchReport();
        if (d.status === "ready") { stopPoll(); setState({ status: "ready", report: d.report }); toast.success("Your coaching report is ready"); }
        else if (d.status === "error") { stopPoll(); setState({ status: "not_generated", report: null }); toast.error("Report generation failed. Please try again."); }
      } catch (e) { /* keep polling */ }
    }, 5000);
  };

  const generate = async () => {
    setState((s) => ({ ...s, status: "generating" }));
    try {
      const res = await api.post(`/sessions/${sessionId}/coaching-report`);
      if (res.data.status === "ready") { setState({ status: "ready", report: res.data.report }); }
      else { startPolling(); }
    } catch (err) {
      setState({ status: "not_generated", report: null });
      toast.error(formatApiError(err));
    }
  };

  if (state.status === "loading") {
    return <div className="flex items-center justify-center py-16 text-stone-500" data-testid="coaching-report-loading"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…</div>;
  }

  if (state.status === "ready" && state.report) {
    return <ReportBody r={state.report} sessionId={sessionId} />;
  }

  if (state.status === "generating") {
    return (
      <div className="bg-white border border-stone-200 rounded-3xl p-10 text-center" data-testid="coaching-report-generating">
        <Loader2 className="w-8 h-8 text-[#b81f24] mx-auto animate-spin mb-4" />
        <div className="font-display text-xl text-stone-900">Your coach is writing your report…</div>
        <p className="text-stone-500 text-sm mt-2">Analysing the full conversation across 14 dimensions. This usually takes 1–2 minutes.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone-200 rounded-3xl p-10 text-center" data-testid="coaching-report-empty">
      <div className="w-12 h-12 rounded-2xl bg-[#b81f24]/10 grid place-items-center mx-auto mb-4">
        <Sparkles className="w-6 h-6 text-[#b81f24]" strokeWidth={1.75} />
      </div>
      <div className="font-display text-xl text-stone-900">Full AI Coaching Report</div>
      <p className="text-stone-500 text-sm mt-2 max-w-md mx-auto">
        Get a personalized, evidence-based coaching report — competency analysis, behavioural strengths, growth
        opportunities, a conversation timeline, expert examples, reflection questions and a tailored action plan.
      </p>
      <Button onClick={generate} data-testid="generate-coaching-report-btn"
        className="mt-5 rounded-md bg-[#b81f24] hover:bg-[#9a1a1f] text-white h-11 px-6">
        <Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Generate full coaching report
      </Button>
    </div>
  );
}
